/* eslint-disable jsx-a11y/no-autofocus */
import * as React from 'react';

import { Box, BoxProps, Flex } from '@opendoor/bricks/core';
import type { EventTrackingFn } from '@opendoor/shared-fe/build/declarations';

import {
  DropdownNumberRangeQuestion,
  FlowQuestion,
  ImageSelectQuestion,
  NumberRangeQuestion,
} from 'src/lib/flow/FlowQuestion';

import ExclusivesCheckboxGroup from '../exclusives/fields/ExclusivesCheckboxGroup';
import ExclusivesRadioGroup from '../exclusives/fields/ExclusivesRadioGroup';
import ExclusivesIntro from '../exclusives/pages/ExclusivesIntro';
import ExclusivesOverview from '../exclusives/pages/ExclusivesOverview';
import ExclusivesInformPage from '../exclusives/pages/InformPage';
import InitialSellingOptionsInterestPage from '../exclusives/pages/InitialSellingOptionsInterestPage';
import BuilderPartnersSelectInput from '../fields/BuilderPartnersSelectInput';
import CheckboxGroup from '../fields/CheckboxGroup';
import CurrencyInput from '../fields/CurrencyInput';
import EmailInput from '../fields/EmailInput';
import ImageSelectInput from '../fields/ImageSelectInput';
import MlsNamesSelectInput from '../fields/MlsNamesSelectInput';
import NumberInput from '../fields/NumberInput';
import NumberRangeInput from '../fields/NumberRangeInput';
import NumberRangeSelectInput from '../fields/NumberRangeSelectInput';
import PhoneInput from '../fields/PhoneInput';
import RadioGroup from '../fields/RadioGroup';
import SelectInput from '../fields/SelectInput';
import TextInput from '../fields/TextInput';

export type IProps = {
  question: FlowQuestion;
  answers: { [key: string]: any };
  onAnswerChange: (key: string, value: any, hasError?: boolean, errorMessage?: string) => void;
  questionTitle: string;

  // Optional:
  errorMessage?: string;
  showInfoCard?: boolean;
  pageHasError?: boolean;
  trackEvent?: EventTrackingFn;

  //needed for recursive rendering?
  autoFocus?: boolean;
  hasError?: boolean;
  unboundedWidth?: boolean;
  isFollowUp?: boolean;
  inputLabelSize?: 'small' | 'medium' | 'large';
};

interface IState {
  blurTimeoutId?: number;
  shouldDisplayError: boolean;
  shouldOverridePrefillWithUndefined: boolean;
}

export default class Question extends React.Component<IProps, IState> {
  public static defaultProps: Partial<IProps> = {
    autoFocus: false,
    hasError: false,
    inputLabelSize: 'small',
    pageHasError: false,
    unboundedWidth: false,
    showInfoCard: true,
    isFollowUp: false,
  };

  public state = {
    blurTimeoutId: undefined,
    shouldDisplayError: false,
    shouldOverridePrefillWithUndefined: false,
  };

  public componentWillUnmount() {
    if (this.state.blurTimeoutId) {
      clearTimeout(this.state.blurTimeoutId);
    }
  }

  public render(): JSX.Element {
    return (
      <Flex className="od-question" width="100%" flexDirection="column">
        {this.renderQuestionComponent()}
      </Flex>
    );
  }

  private renderQuestionComponent = (): JSX.Element | null => {
    const { answers, hasError, pageHasError, question, isFollowUp, questionTitle } = this.props;
    const { key } = question;
    const answer = answers[key];

    const followUpStyles: BoxProps = {
      ...(isFollowUp && {
        backgroundColor: 'neutrals10',
        borderRadius: 'roundedSquare',
        px: 5,
        pt: 4,
        width: '100%',
      }),
    };

    switch (question.type) {
      // previous these were custom questions
      case 'intial_selling_options_interest':
        return (
          <InitialSellingOptionsInterestPage
            question={question}
            values={answers[question.key]}
            fieldsetLegend={questionTitle}
          />
        );
      // end custom questions

      case 'range':
        return <NumberRangeInput question={question as NumberRangeQuestion} value={answer} />;
      case 'dropdown_number_range':
        return (
          <NumberRangeSelectInput
            question={question as DropdownNumberRangeQuestion}
            value={answer}
          />
        );
      case 'multi_select':
        // TODO: rework our container css so that the width here can be set in
        // a parent element, this will allow multi_select and checkbox to be
        // the exact same

        return (
          <Box maxWidth={['325px', '450px']} width={['100%', '450px']} {...followUpStyles}>
            <CheckboxGroup
              question={question}
              values={answer}
              fieldsetLegend={questionTitle || question.label}
              hasError={!!hasError}
              pageHasError={!!pageHasError}
              isFollowUp={!!isFollowUp}
            />
          </Box>
        );
      case 'exclusives_multi_select':
        return (
          <ExclusivesCheckboxGroup
            question={question}
            values={answer}
            fieldsetLegend={questionTitle}
            hasError={!!hasError}
            pageHasError={!!pageHasError}
            isFollowUp={!!isFollowUp}
          />
        );
      case 'radio_single_select':
        return (
          <RadioGroup
            // props about the question
            label={question.label}
            tooltip={question.helperText}
            name={key}
            options={question.options}
            // contextual props
            fieldsetLegend={questionTitle}
            value={answer}
            hasError={!!hasError}
            pageHasError={!!pageHasError}
            isFollowUp={!!isFollowUp}
          />
        );
      case 'exclusives_radio_single_select':
        return (
          <ExclusivesRadioGroup
            // props about the question
            label={question.label}
            tooltip={question.helperText}
            name={key}
            options={question.options}
            // contextual props
            fieldsetLegend={questionTitle}
            value={answer}
            hasError={!!hasError}
            pageHasError={!!pageHasError}
            isFollowUp={!!isFollowUp}
          />
        );
      case 'combo_input':
        if (question.optionsDataSource == 'builder_partners') {
          return <BuilderPartnersSelectInput question={question} value={answer} />;
        } else if (question.optionsDataSource == 'mls_names') {
          return <MlsNamesSelectInput question={question} value={answer} />;
        }
        return null;
      case 'dropdown_select':
        return <SelectInput question={question} value={answer} />;
      case 'email':
        return <EmailInput question={question} value={answer} />;
      case 'integer':
        return <NumberInput question={question} value={answer} />;
      case 'phone':
        return <PhoneInput question={question} value={answer} />;
      case 'currency':
        return <CurrencyInput question={question} value={answer} />;
      case 'single_line':
        return <TextInput question={question} value={answer} />;

      case 'image_single_select':
        return <ImageSelectInput question={question as ImageSelectQuestion} value={answer} />;
      case 'element':
        if (question.component == 'exclusives_intro') {
          return <ExclusivesIntro questionKey={question.key} />;
        }
        if (question.component == 'exclusives_inform') {
          return <ExclusivesInformPage questionKey={question.key} />;
        }
        if (question.component == 'exclusives_overview') {
          return <ExclusivesOverview />;
        }
        // eslint-disable-next-line no-console
        console.error('Unsupported question.component for question.type=="element"', question);
        return null;
      default:
        /* eslint-disable-next-line no-console */
        console.error(`Got question, but do not know how render its type`, question);
        // TODO - log to sentry?
        return null;
    }
  };
}

export const Questions: React.FC<{
  answers: { [key: string]: any };
  onAnswerChange: (key: string, value: any, hasError?: boolean, errorMessage?: string) => void;
  questions: Array<FlowQuestion>;
  hasError?: boolean;
  pageHasError?: boolean;
  isNestedFollowup?: boolean;
}> = ({ answers, hasError, onAnswerChange, pageHasError, questions, isNestedFollowup }) => {
  const visibleQuestions = questions.filter((q) => !q.hidden);
  if (visibleQuestions.length === 0) {
    return null;
  }
  return (
    <Box
      cursor="auto"
      mt={2}
      pt={isNestedFollowup ? 0 : 6}
      pb={isNestedFollowup ? 0 : 2}
      width="100%"
    >
      {questions.map((f, index) => {
        return (
          <Box key={f.key} mt={index > 0 ? 4 : 0}>
            <Question
              answers={answers}
              hasError={hasError}
              onAnswerChange={onAnswerChange}
              pageHasError={pageHasError}
              question={{ ...f }}
              unboundedWidth
              isFollowUp={true}
            />
          </Box>
        );
      })}
    </Box>
  );
};
